import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  addEdge,
  useNodesState,
  useEdgesState,
  getSmoothStepPath
} from 'reactflow';
import 'reactflow/dist/style.css';
import { PlusIcon, TrashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../context/ProjectContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { supabase } from '../../lib/supabase';
import AddItemDrawer from './AddItemDrawer';
import WorkflowSettingsModal from './WorkflowSettingsModal';
import { canConnect } from './workflowRules';


// Custom edge with delete button
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data
}) => {
  const [showDelete, setShowDelete] = useState(false);
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 0,
    orientation: data?.isVertical ? 'vertical' : 'horizontal'
  });

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path cursor-pointer hover:stroke-accent-primary"
        d={edgePath}
        style={{
          ...style,
          strokeWidth: 3,
          stroke: '#6366f1',
          opacity: 1
        }}
        onMouseEnter={() => setShowDelete(true)}
        onMouseLeave={() => setShowDelete(false)}
      />
      {showDelete && (
        <foreignObject
          width={20}
          height={20}
          x={labelX - 10}
          y={labelY - 10}
          className="cursor-pointer"
          onMouseEnter={() => setShowDelete(true)}
          onMouseLeave={() => setShowDelete(false)}
          onClick={(event) => {
            event.stopPropagation();
            data?.onDelete(id);
          }}
        >
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 transition-colors">
            <TrashIcon className="w-3 h-3 text-white" />
          </div>
        </foreignObject>
      )}
    </>
  );
};


const edgeTypes = {
  custom: CustomEdge
};


export default function Structure() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [channels, setChannels] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { currentProject } = useProject();
  const { confirm } = useConfirm();
  const { addToast } = useToast();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [workflowSettings, setWorkflowSettings] = useState({
    name: 'Content Workflow',
    description: '',
    logOutput: false,
    isLive: false
  });

  useEffect(() => {
    if (currentProject?.id) {
      fetchChannels();
    }
  }, [currentProject?.id]);

  useEffect(() => {
    // Add trigger node if this is a new workflow with no nodes
    if (nodes.length === 0) {
      const triggerNode = {
        id: `trigger-${Date.now()}`,
        type: 'trigger',
        position: { x: 100, y: 100 },
        data: { 
          label: 'Workflow Trigger',
          category: 'trigger',
          triggerType: 'manual',
          scheduleType: 'datetime',
          onDelete: handleDeleteNode,
          onRename: handleRenameNode,
          onUpdateTiming: handleUpdateTiming
        }
      };

      setNodes([triggerNode]);
    }
  }, []); // Empty dependency array means this runs once on mount

  const fetchChannels = async () => {
    try {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .eq('project_id', currentProject.id)
        .eq('status', 'active');

      if (error) throw error;
      setChannels(data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const handleAddItem = (newItem) => {
    // Calculate position based on existing nodes
    const existingNodes = nodes.length;
    const position = {
      x: 100 + (existingNodes * 200),
      y: 100
    };

    console.log('Creating new node with data:', newItem);

    const newNode = {
      id: `${newItem.type}-${Date.now()}`,
      type: newItem.type,
      position: position,
      data: { 
        ...newItem,
        label: newItem.name,
        type: newItem.type,
        category: newItem.category || 'agent',
        onDelete: handleDeleteNode,
        onRename: handleRenameNode,
        onUpdateTiming: handleUpdateTiming
      }
    };

    console.log('Final node structure:', newNode);

    setNodes(prev => [...prev, newNode]);
    setShowAddModal(false);
  };

  const handleDeleteNode = useCallback((nodeId) => {
    setNodes(prev => prev.filter(node => node.id !== nodeId));
    setEdges(prev => prev.filter(edge => 
      edge.source !== nodeId && edge.target !== nodeId
    ));
  }, [setNodes, setEdges]);

  const handleRenameNode = useCallback((nodeId, newName) => {
    setNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, data: { ...node.data, label: newName } }
        : node
    ));
  }, [setNodes]);

  const handleUpdateTiming = useCallback((nodeId, timing) => {
    console.log('Structure handleUpdateTiming:', { 
      nodeId, 
      timing,
      currentNodes: nodes 
    });
    
    setNodes(prev => {
      const updatedNodes = prev.map(node => {
        if (node.id === nodeId) {
          // Log the node before update
          console.log('Updating node:', {
            before: node,
            timing: timing
          });

          // Create a new node with all data preserved
          const updatedNode = {
            ...node,
            data: {
              ...node.data,  // Keep all existing data
              days: timing.days || 0,
              time: timing.time || '12:00',
              connectionId: timing.connectionId || '',
              label: timing.label || node.data.label,
              // Make sure we keep these properties
              category: node.data.category,
              channelType: node.data.channelType,
              onDelete: node.data.onDelete,
              onRename: node.data.onRename,
              onUpdateTiming: node.data.onUpdateTiming
            }
          };

          // Log the node after update
          console.log('Node updated:', {
            after: updatedNode,
            hasConnectionId: Boolean(updatedNode.data.connectionId)
          });

          return updatedNode;
        }
        return node;
      });

      // Log the final nodes state
      console.log('Final nodes state:', updatedNodes);
      return updatedNodes;
    });
  }, [nodes, setNodes]);

  const onConnect = useCallback((params) => {
    // Get the source and target nodes
    const sourceNode = nodes.find(node => node.id === params.source);
    const targetNode = nodes.find(node => node.id === params.target);

    if (!sourceNode || !targetNode) return;

    console.log('Attempting to connect nodes:', {
      sourceNode: {
        id: sourceNode.id,
        type: sourceNode.type,
        category: sourceNode.data?.category,
        data: sourceNode.data
      },
      targetNode: {
        id: targetNode.id,
        type: targetNode.type,
        category: targetNode.data?.category,
        data: targetNode.data
      }
    });

    // Check for existing connection
    const hasExistingConnection = edges.some(edge => 
      (edge.source === params.source && edge.target === params.target) ||
      (edge.source === params.target && edge.target === params.source)
    );

    if (hasExistingConnection) {
      addToast('These items are already connected', 'error');
      return;
    }

    // Check if connection is allowed
    const isAllowed = canConnect(sourceNode, targetNode);
    console.log('Connection allowed:', isAllowed);

    if (!isAllowed) {
      addToast('This connection is not allowed', 'error');
      return;
    }

    // If all checks pass, create the connection
    setEdges(prev => addEdge({
      ...params,
      type: 'custom',
      animated: false,
      style: { 
        stroke: '#6366f1',
        strokeWidth: 3,
        opacity: 1
      },
      data: { 
        onDelete: handleDeleteEdge,
        sourceType: sourceNode.type,
        targetType: targetNode.type,
        sourceCategory: sourceNode.data?.category,
        targetCategory: targetNode.data?.category
      }
    }, prev));
  }, [nodes, edges, addToast, handleDeleteEdge]);

  const handleDeleteEdge = useCallback(async (edgeId) => {
    const confirmed = await confirm({
      title: 'Delete Connection',
      message: 'Are you sure you want to delete this connection?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      setEdges(prev => prev.filter(e => e.id !== edgeId));
    }
  }, [confirm, setEdges]);

  const handleSettingsUpdate = (newSettings) => {
    setWorkflowSettings(newSettings);
    addToast('Workflow settings updated', 'success');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-semibold">{workflowSettings.name}</h1>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            workflowSettings.isLive 
              ? 'bg-green-500/10 text-green-500 border border-green-500/20' 
              : 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20'
          }`}>
            {workflowSettings.isLive ? 'Live Mode' : 'Draft Mode'}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 hover:bg-dark-300 rounded-lg transition-colors"
            title="Workflow Settings"
          >
            <Cog6ToothIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Item</span>
          </button>
        </div>
      </div>

      {/* Flow Diagram */}
      <div className="h-[calc(100vh-12rem)] bg-dark-200 rounded-lg overflow-hidden">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{
            type: 'custom'
          }}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          minZoom={0.1}
          maxZoom={1.5}
          fitView
          className="bg-dark-200"
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>

      {/* Add Item Drawer */}
      <AddItemDrawer
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddItem}
        channels={channels}
        existingNodes={nodes}
      />

      {/* Add Settings Modal */}
      {showSettingsModal && (
        <WorkflowSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          settings={workflowSettings}
          onSave={handleSettingsUpdate}
        />
      )}
    </div>
  );
} 